import type { Pixel } from "@pdfking/renderer-api"
import { BoxToRectangle } from "@pdfking/renderer-api"
import type {
  PdfiumApi,
  PdfiumFunctions,
  PdfiumTypeMap,
  Pointer,
} from "@pdfking/renderer-wasm-pdfium"
import type { Bytes } from "@pdfking/tfunc"
import { ToBlob } from "@pdfking/tfunc"

type PointerDoc = PdfiumTypeMap["FPDF_DOCUMENT"]
type PointerPage = PdfiumTypeMap["FPDF_PAGE"]

type Box = [Pixel, Pixel, Pixel, Pixel]

const get_page_box = async (
  { malloc, call, readFloats }: PdfiumApi,
  page: PointerPage,
  method: PdfiumFunctions
): Promise<Box | undefined> => {
  const allocs = [
    malloc(4 as Bytes),
    malloc(4 as Bytes),
    malloc(4 as Bytes),
    malloc(4 as Bytes),
  ] as const
  const pointers = allocs.map(a => a.pointer) as [
    Pointer,
    Pointer,
    Pointer,
    Pointer,
  ]

  const success = await call(method, page, ...pointers)

  if (!success) {
    allocs.forEach(a => a.free())
    return
  }

  const points = pointers.map(p => readFloats(p, 1)[0])
  allocs.forEach(a => a.free())

  return points as Box
}

export const ExtractPage = async (
  pdfium: PdfiumApi,
  doc_pointer: PointerDoc,
  page: number
) => {
  const {
    malloc,
    call,
    safeCall,
    readFloats,
    writeString,
    readBytes,
    addCallback,
    setInt32,
  } = pdfium

  const new_doc_pointer = await safeCall("FPDF_CreateNewDocument")

  const { pointer: pageRangePtr, free: freePageRange } = writeString(`${page}`)

  await safeCall(
    "FPDF_ImportPages",
    new_doc_pointer,
    doc_pointer,
    pageRangePtr,
    0
  )

  freePageRange()

  const imported_page = await safeCall("FPDF_LoadPage", new_doc_pointer, 0)

  const { pointer: size_pointer, free: freeSize } = malloc(8 as Bytes)
  await safeCall(
    "FPDF_GetPageSizeByIndexF",
    doc_pointer,
    page - 1,
    size_pointer
  )

  const [width, height] = readFloats(size_pointer, 2)
  const media_box: Box = [0, 0, width, height] as Box

  freeSize()

  const trim_box =
    (await get_page_box(pdfium, imported_page, "FPDFPage_GetTrimBox")) ??
    media_box

  await call("FPDF_ClosePage", imported_page)

  const chunks: Uint8Array[] = []

  const write_callback = (_: number, data: number, size: number) => {
    chunks.push(readBytes(data as Pointer, size as Bytes))
    return size
  }

  const { pointer: callbackPtr, free: freeCallback } = addCallback(
    write_callback,
    "iiii"
  )
  const { pointer: structPtr, free: freeStruct } = malloc(8 as Bytes)

  setInt32(structPtr, 1)
  setInt32((structPtr + 4) as Pointer, callbackPtr)

  await safeCall("FPDF_SaveAsCopy", new_doc_pointer, structPtr, 2)

  freeStruct()
  freeCallback()
  await call("FPDF_CloseDocument", new_doc_pointer)

  return {
    stream: [ToBlob(chunks, "application/pdf")],
    trim_box: BoxToRectangle(trim_box),
    media_box: BoxToRectangle(media_box),
  }
}
